import { useState, useEffect } from 'react';
import { Table, Pagination, Spin, message, Button, Modal, Form, Input, Select } from 'antd';
import { axiosGet, axiosPost } from '../services/http.services';
import {
	API_GET_ALL_USERS,
	API_CREATE_USER,
	API_DELETE_USER,
	API_RESET_PASSWORD,
	API_GET_DATA,
} from '../api/api';
import { Trash } from 'lucide-react';
import CustomFilter from '../common/CustomFilter';

const AccessManagement = () => {
	const [airportOptions, setAirportOptions] = useState([]);
	const [users, setUsers] = useState([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState(null);
	const [form] = Form.useForm();
	const [passwordForm] = Form.useForm();

	useEffect(() => {
		fetchUsers();
		fetchDataApi();
	}, [currentPage, pageSize]);

	const fetchDataApi = async () => {
		try {
			setLoading(true)
			const response = await axiosGet(API_GET_DATA);
			if (response?.status === 200) {
				setLoading(false)
				setAirportOptions(response?.data?.Airport)
			}
		} catch (error) {
			setLoading(false)
			console.log(error);
		}
	}

	const fetchUsers = async () => {
		setLoading(true);
		try {
			const response = await axiosGet(API_GET_ALL_USERS, {
				params: {
					page: currentPage,
					limit: pageSize,
				},
			});
			setUsers(response.data.users);
			setTotal(response.data.totalPages * pageSize);
		} catch (error) {
			message.error('Error fetching users');
		} finally {
			setLoading(false);
		}
	};

	const handleCreateUser = async (values) => {
		setLoading(true);
		try {
			await axiosPost(API_CREATE_USER, values);
			message.success('User created successfully!');
			fetchUsers();
			setIsModalVisible(false);
			form.resetFields();
		} catch (error) {
			message.error('Error creating user');
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = (record) => {
		Modal.confirm({
			title: 'Are you sure you want to delete this user?',
			content: `User: ${record[1]} (${record[2]})`,
			onOk: async () => {
				setLoading(true);
				try {
					await axiosPost(API_DELETE_USER, { userid: record[0] });
					message.success('User deleted successfully!');
					fetchUsers();
				} catch (error) {
					message.error('Error deleting user');
				} finally {
					setLoading(false);
				}
			},
			onCancel() {
			},
		});
	};

	const handleResetPassword = async () => {
		try {
			await axiosPost(API_RESET_PASSWORD, { userid: selectedUserId, password: passwordForm.getFieldValue('password') });
			message.success('Password reset successfully!');
			setIsPasswordModalVisible(false);
			passwordForm.resetFields();
		} catch (error) {
			message.error('Error resetting password');
		}
	};

	const handlePasswordModalOpen = (userId) => {
		setSelectedUserId(userId);
		setIsPasswordModalVisible(true);
	};

	const handlePageChange = (page, pageSize) => {
		setCurrentPage(page);
		setPageSize(pageSize);
	};

	const dynamicColumn = (preferredOrder, includeDeleteColumn = true) => {
		const columns = preferredOrder?.map((key, index) => {
			return {
				title: <CustomFilter key={index} title={key?.replace(/_/g, ' ')?.toUpperCase()} />,
				align: "center",
				dataIndex: index,
				key: index,
				render: (text) => {
					if (key === 'userid') {
						return text?.split("-")[0]
					}
					if (key === 'created_at') {
						const date = new Date(text);
						return date.toLocaleString();
					}
					if (key === 'airports') {
						const airportNames = JSON.parse(text || "[]");
						return airportNames?.length > 0 ? airportNames?.join(', ') : "All";
					}
					return text
				}
			};
		});

		if (includeDeleteColumn) {
			columns.push({
				title: "Actions",
				key: "action",
				render: (_, record) => {
					return (
						<div style={{ display: "flex" }}>
							<Button
								onClick={() => handlePasswordModalOpen(record[0])}
								style={{ width: "90%", marginInline: "5px" }}
								className='text-center'
							>
								Reset Password
							</Button>
							{(record?.[1] !== 'Digiyatra-Admin' && record?.[2] !== 'Digiyatra-Admin') && <Button
								onClick={() => handleDelete(record)}
								danger
								style={{ width: "90%", marginInline: "5px" }}
								className='text-center'
							>
								<Trash />
							</Button>}
						</div>
					);
				},
			});
		}

		return columns;
	};

	return (
		<div>
			<Button type="primary" onClick={() => setIsModalVisible(true)}>
				Create User
			</Button>

			<div style={{ marginTop: 30, marginBottom: 30, boxShadow: '0 1px 5px rgba(0, 0, 0, 0.2)', borderRadius: 15, overflow: 'hidden' }}>
				<Spin spinning={loading}>
					<Table
						columns={dynamicColumn(["userid", "name", "username", "role", "created_at", "airports"])}
						dataSource={users}
						rowKey="userid"
						pagination={false}
						loading={loading}
					/>
				</Spin>
			</div>

			<Pagination
				current={currentPage}
				pageSize={pageSize}
				total={total}
				onChange={handlePageChange}
				showSizeChanger
				pageSizeOptions={['50', '100']}
				showTotal={(total) => `Total ${total} items`}
			/>

			<Modal
				title="Create User"
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				footer={null}
			>
				<Form
					form={form}
					onFinish={handleCreateUser}
					layout="vertical"
				>
					<Form.Item
						label="Name"
						name="name"
						rules={[{ required: true, message: 'Please input the user name!' }]}
					>
						<Input />
					</Form.Item>

					<Form.Item
						label="Username"
						name="username"
						rules={[{ required: true, message: 'Please input the username!' }]}
					>
						<Input />
					</Form.Item>

					<Form.Item
						label="Role"
						name="role"
						rules={[{ required: true, message: 'Please select a role!' }]}
					>
						<Select placeholder="Select a role">
							<Select.Option value="admin">Admin</Select.Option>
							<Select.Option value="staff">Staff</Select.Option>
						</Select>
					</Form.Item>

					<Form.Item
						label="Airports"
						name="airports"
						rules={[{ required: false }]}
					>
						<Select
							mode="multiple"
							placeholder="Select airports"
							options={airportOptions}
						/>
					</Form.Item>

					<Form.Item>
						<Button type="primary" htmlType="submit" style={{ width: '100%' }}>
							Create User
						</Button>
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title="Reset Password"
				open={isPasswordModalVisible}
				onCancel={() => setIsPasswordModalVisible(false)}
				footer={null}
			>
				<Form
					form={passwordForm}
					onFinish={handleResetPassword}
					layout="vertical"
				>
					<Form.Item
						label="New Password"
						name="password"
						rules={[{ required: true, message: 'Please input the new password!' }]}
					>
						<Input.Password />
					</Form.Item>

					<Form.Item>
						<Button type="primary" htmlType="submit" style={{ width: '100%' }}>
							Reset Password
						</Button>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
};

export default AccessManagement;
